import { useEffect, useRef, type ReactNode } from 'react';
import { cn, Flex } from '@kognitos/lattice';

/**
 * Props for the Overlay component.
 */
interface IOverlayProps {
  /** Callback to close the layover (triggered on backdrop click) */
  onClose: () => void;
  /** Sizing overrides for the content box, e.g. `max-w-4xl max-h-[70vh]` */
  className?: string;
  /** Layover content rendered inside the backdrop */
  children: ReactNode;
  /** Id of the visible title labelling the dialog. */
  ariaLabelledBy?: string;
  /**
   * `lg` trims the backdrop's padding and lets the content box grow to fill it,
   * for content that needs the room (the browser preview). Defaults to `md`.
   */
  size?: 'md' | 'lg';
}

/**
 * Full-screen backdrop that closes the layover on click.
 * Provides a translucent dialog backdrop for all layover variants.
 *
 * Uses z-index 100 and pointer-events: auto to ensure the overlay renders above
 * and remains interactive over Radix Sheet modals (which set pointer-events: none on body).
 * The onWheel handler and overscroll-behavior prevent the Sheet's scroll lock from
 * intercepting trackpad scroll gestures meant for content inside the overlay.
 *
 * @param props - Component props
 * @returns Full-screen overlay wrapper
 */
export function Overlay({
  onClose,
  className,
  children,
  ariaLabelledBy,
  size = 'md',
}: IOverlayProps) {
  const isLarge = size === 'lg';
  const dialogRef = useRef<HTMLDivElement>(null);
  const dismissPointerDown = useRef(false);

  // Keep the underlying run page stationary while the modal owns scrolling.
  useEffect(() => {
    const bodyOverflow = document.body.style.overflow;
    const documentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = documentOverflow;
    };
  }, []);

  // eslint-disable-next-line no-restricted-syntax -- reason: Dialog focus trapping and focus restoration require imperative DOM focus management.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    if (!dialog) return;
    // Queried separately: a combined selector list resolves in document order, not selector order, so it could focus the wrong control.
    const focusable =
      dialog.querySelector<HTMLElement>('[data-output-modal-autofocus]') ??
      dialog.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
    focusable?.focus();

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener('keydown', trapFocus);
    return () => {
      dialog.removeEventListener('keydown', trapFocus);
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <Flex
      justify="center"
      align="center"
      position="fixed"
      paddingX={32}
      /* theme-fixed: the scrim must stay dark in either theme, or a light
         backdrop over light content stops reading as a backdrop at all. */
      className={cn(
        'output-modal-backdrop z-100 inset-0 bg-black/55 backdrop-blur-xs pointer-events-auto overscroll-contain',
        // pb-16 still clears the carousel pinned to the bottom (bottom-4 + h-10).
        isLarge ? 'pt-8 pb-16' : 'pt-14 pb-22',
      )}
      onPointerDownCapture={event => {
        dismissPointerDown.current =
          event.target === event.currentTarget ||
          event.target === dialogRef.current;
      }}
      onClick={event => {
        if (dismissPointerDown.current && event.target === event.currentTarget)
          onClose();
        dismissPointerDown.current = false;
      }}
      onWheel={e => e.stopPropagation()}
    >
      <Flex
        ref={dialogRef}
        direction="column"
        gap={2}
        width="full"
        align="stretch"
        overflow="visible"
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        className={cn(
          'output-modal-shell min-h-0',
          isLarge && 'h-full max-w-[1440px]',
          className,
        )}
        onClick={event => {
          if (
            dismissPointerDown.current &&
            event.target === event.currentTarget
          )
            onClose();
          dismissPointerDown.current = false;
          event.stopPropagation();
        }}
      >
        {children}
      </Flex>
    </Flex>
  );
}
